import PropTypes from 'prop-types';
import React__default, { useContext, createContext, forwardRef, useImperativeHandle, useRef, useEffect, useState, useMemo, cloneElement } from 'react';
import { h as _createClass, b as _typeof, i as _classCallCheck, d as _objectSpread2, a as _slicedToArray } from './_rollupPluginBabelHelpers-e71b1aad.js';
import ReactDOM from 'react-dom';
import L$1, { Control, Map as Map$2 } from 'leaflet';
import 'react-leaflet-markercluster';

const CONTEXT_VERSION = 1;
const LeafletContext = /*#__PURE__*/createContext(null);
const LeafletProvider = LeafletContext.Provider;
function useLeafletContext() {
  const context = useContext(LeafletContext);

  if (context == null) {
    throw new Error('No context provided: useLeafletContext() can only be used in a descendant of <MapContainer>');
  }

  return context;
}

function createLeafComponent(useElement) {
  function LeafComponent(props, ref) {
    const {
      instance
    } = useElement(props).current;
    useImperativeHandle(ref, () => instance);
    return null;
  }

  return /*#__PURE__*/forwardRef(LeafComponent);
}

function createControlHook(useElement) {
  return function useLeafletControl(props) {
    const context = useLeafletContext();
    const elementRef = useElement(props, context);
    const {
      instance
    } = elementRef.current;
    const positionRef = useRef(props.position);
    const {
      position
    } = props;
    useEffect(function addControl() {
      instance.addTo(context.map);
      return function removeControl() {
        instance.remove();
      };
    }, [context.map, instance]);
    useEffect(function updateControl() {
      if (position != null && position !== positionRef.current) {
        instance.setPosition(position);
        positionRef.current = position;
      }
    }, [instance, position]);
    return elementRef;
  };
}

function createElementHook(createElement, updateElement) {
  if (updateElement == null) {
    return function useImmutableLeafletElement(props, context) {
      return useRef(createElement(props, context));
    };
  }

  return function useMutableLeafletElement(props, context) {
    const elementRef = useRef(createElement(props, context));
    const propsRef = useRef(props);
    const {
      instance
    } = elementRef.current;
    useEffect(function updateElementProps() {
      if (propsRef.current !== props) {
        updateElement(instance, props, propsRef.current);
        propsRef.current = props;
      }
    }, [instance, props, context]);
    return elementRef;
  };
}

function createControlComponent(createInstance) {
  function createElement(props, context) {
    return {
      instance: createInstance(props),
      context
    };
  }

  const useElement = createElementHook(createElement);
  const useControl = createControlHook(useElement);
  return createLeafComponent(useControl);
}

function useMap() {
  return useLeafletContext().map;
}

const AttributionControl = createControlComponent(function createAttributionControl(props) {
  return new Control.Attribution(props);
});

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function useMapElement(mapRef, props) {
  const [map, setMap] = useState(null);
  useEffect(() => {
    if (mapRef.current !== null && map === null) {
      const instance = new Map$2(mapRef.current, props);

      if (props.center != null && props.zoom != null) {
        instance.setView(props.center, props.zoom);
      } else if (props.bounds != null) {
        instance.fitBounds(props.bounds, props.boundsOptions);
      }

      if (props.whenReady != null) {
        instance.whenReady(props.whenReady);
      }

      setMap(instance);
    }
  }, [mapRef, map, props]);
  return map;
}
function MapContainer({
  children,
  className,
  id,
  placeholder,
  style,
  whenCreated,
  ...options
}) {
  const mapRef = useRef(null);
  const map = useMapElement(mapRef, options);
  const createdRef = useRef(false);
  useEffect(() => {
    if (map != null && createdRef.current === false && whenCreated != null) {
      createdRef.current = true;
      whenCreated(map);
    }
  }, [map, whenCreated]);
  const [props] = useState({
    className,
    id,
    style
  });
  const context = useMemo(() => map ? {
    __version: CONTEXT_VERSION,
    map
  } : null, [map]);
  const contents = context ? /*#__PURE__*/React__default.createElement(LeafletProvider, {
    value: context
  }, children) : placeholder != null ? placeholder : null;
  return /*#__PURE__*/React__default.createElement("div", _extends({}, props, {
    ref: mapRef
  }), contents);
}

const ZoomControl = createControlComponent(function createZoomControl(props) {
  return new Control.Zoom(props);
});

var markersShape = PropTypes.arrayOf(PropTypes.shape({
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  id: PropTypes.string
}));

// This implementation of LRUMap is a copy of https://github.com/rsms/js-lru/ ,
// trivially adapted for ES6 exports.

/*
The MIT License

Copyright (c) 2010-2020 Rasmus Andersson <https://rsms.me/>

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/

/**
 * A doubly linked list-based Least Recently Used (LRU) cache. Will keep most
 * recently used items while discarding least recently used items when its limit
 * is reached.
 *
 * Licensed under MIT. Copyright (c) 2010 Rasmus Andersson <http://hunch.se/>
 * See README.md for details.
 *
 * Illustration of the design:
 *
 *       entry             entry             entry             entry
 *       ______            ______            ______            ______
 *      | head |.newer => |      |.newer => |      |.newer => | tail |
 *      |  A   |          |  B   |          |  C   |          |  D   |
 *      |______| <= older.|______| <= older.|______| <= older.|______|
 *
 *  removed  <--  <--  <--  <--  <--  <--  <--  <--  <--  <--  <--  added
 */

var NEWER = Symbol('newer');
var OLDER = Symbol('older');
/* eslint-disable */
var LRUMap = /*#__PURE__*/function () {
  function LRUMap(limit, entries) {
    _classCallCheck(this, LRUMap);
    if (typeof limit !== 'number') {
      // called as (entries)
      entries = limit;
      limit = 0;
    }
    this.size = 0;
    this.limit = limit;
    this.oldest = this.newest = undefined;
    this._keymap = new Map();
    if (entries) {
      this.assign(entries);
      if (limit < 1) {
        this.limit = this.size;
      }
    }
  }
  return _createClass(LRUMap, [{
    key: "_markEntryAsUsed",
    value: function _markEntryAsUsed(entry) {
      if (entry === this.newest) {
        // Already the most recenlty used entry, so no need to update the list
        return;
      }
      // HEAD--------------TAIL
      //   <.older   .newer>
      //  <--- add direction --
      //   A  B  C  <D>  E
      if (entry[NEWER]) {
        if (entry === this.oldest) {
          this.oldest = entry[NEWER];
        }
        entry[NEWER][OLDER] = entry[OLDER]; // C <-- E.
      }
      if (entry[OLDER]) {
        entry[OLDER][NEWER] = entry[NEWER]; // C. --> E
      }
      entry[NEWER] = undefined; // D --x
      entry[OLDER] = this.newest; // D. --> E
      if (this.newest) {
        this.newest[NEWER] = entry; // E. <-- D
      }
      this.newest = entry;
    }
  }, {
    key: "assign",
    value: function assign(entries) {
      var entry;
      var limit = this.limit || Number.MAX_VALUE;
      this._keymap.clear();
      var it = entries[Symbol.iterator]();
      for (var itv = it.next(); !itv.done; itv = it.next()) {
        var e = new Entry(itv.value[0], itv.value[1]);
        this._keymap.set(e.key, e);
        if (!entry) {
          this.oldest = e;
        } else {
          entry[NEWER] = e;
          e[OLDER] = entry;
        }
        entry = e;
        if (limit-- == 0) {
          throw new Error('overflow');
        }
      }
      this.newest = entry;
      this.size = this._keymap.size;
    }
  }, {
    key: "get",
    value: function get(key) {
      // First, find our cache entry
      var entry = this._keymap.get(key);
      if (!entry) {
        return;
      } // Not cached. Sorry.
      // As <key> was found in the cache, register it as being requested recently
      this._markEntryAsUsed(entry);
      return entry.value;
    }
  }, {
    key: "set",
    value: function set(key, value) {
      var entry = this._keymap.get(key);
      if (entry) {
        // update existing
        entry.value = value;
        this._markEntryAsUsed(entry);
        return this;
      }

      // new entry
      this._keymap.set(key, entry = new Entry(key, value));
      if (this.newest) {
        // link previous tail to the new tail (entry)
        this.newest[NEWER] = entry;
        entry[OLDER] = this.newest;
      } else {
        // we're first in -- yay
        this.oldest = entry;
      }

      // add new entry to the end of the linked list -- it's now the freshest entry.
      this.newest = entry;
      ++this.size;
      if (this.size > this.limit) {
        // we hit the limit -- remove the head
        this.shift();
      }
      return this;
    }
  }, {
    key: "shift",
    value: function shift() {
      // todo: handle special case when limit == 1
      var entry = this.oldest;
      if (entry) {
        if (this.oldest[NEWER]) {
          // advance the list
          this.oldest = this.oldest[NEWER];
          this.oldest[OLDER] = undefined;
        } else {
          // the cache is exhausted
          this.oldest = undefined;
          this.newest = undefined;
        }
        // Remove last strong reference to <entry> and remove links from the purged
        // entry being returned:
        entry[NEWER] = entry[OLDER] = undefined;
        this._keymap.delete(entry.key);
        --this.size;
        return [entry.key, entry.value];
      }
    }

    // -------------------------------------------------------------------------------------
    // Following code (until end of class definition) is optional and can be removed without
    // breaking the core functionality.
  }, {
    key: "find",
    value: function find(key) {
      var e = this._keymap.get(key);
      return e ? e.value : undefined;
    }
  }, {
    key: "has",
    value: function has(key) {
      return this._keymap.has(key);
    }
  }, {
    key: "delete",
    value: function _delete(key) {
      var entry = this._keymap.get(key);
      if (!entry) {
        return;
      }
      this._keymap.delete(entry.key);
      if (entry[NEWER] && entry[OLDER]) {
        // relink the older entry with the newer entry
        entry[OLDER][NEWER] = entry[NEWER];
        entry[NEWER][OLDER] = entry[OLDER];
      } else if (entry[NEWER]) {
        // remove the link to us
        entry[NEWER][OLDER] = undefined;
        // link the newer entry to head
        this.oldest = entry[NEWER];
      } else if (entry[OLDER]) {
        // remove the link to us
        entry[OLDER][NEWER] = undefined;
        // link the newer entry to head
        this.newest = entry[OLDER];
      } else {
        // if(entry[OLDER] === undefined && entry.newer === undefined) {
        this.oldest = this.newest = undefined;
      }
      this.size--;
      return entry.value;
    }
  }, {
    key: "clear",
    value: function clear() {
      // Not clearing links should be safe, as we don't expose live links to user
      this.oldest = this.newest = undefined;
      this.size = 0;
      this._keymap.clear();
    }
  }, {
    key: "keys",
    value: function keys() {
      return new KeyIterator(this.oldest);
    }
  }, {
    key: "values",
    value: function values() {
      return new ValueIterator(this.oldest);
    }
  }, {
    key: "entries",
    value: function entries() {
      return this;
    }
  }, {
    key: Symbol.iterator,
    value: function value() {
      return new EntryIterator(this.oldest);
    }
  }, {
    key: "forEach",
    value: function forEach(fun, thisObj) {
      if (_typeof(thisObj) !== 'object') {
        thisObj = this;
      }
      var entry = this.oldest;
      while (entry) {
        fun.call(thisObj, entry.value, entry.key, this);
        entry = entry[NEWER];
      }
    }

    /** Returns a JSON (array) representation */
  }, {
    key: "toJSON",
    value: function toJSON() {
      var s = new Array(this.size);
      var i = 0;
      var entry = this.oldest;
      while (entry) {
        s[i++] = {
          key: entry.key,
          value: entry.value
        };
        entry = entry[NEWER];
      }
      return s;
    }

    /** Returns a String representation */
  }, {
    key: "toString",
    value: function toString() {
      var s = '';
      var entry = this.oldest;
      while (entry) {
        s += "".concat(String(entry.key), ":").concat(entry.value);
        entry = entry[NEWER];
        if (entry) {
          s += ' < ';
        }
      }
      return s;
    }
  }]);
}();
function Entry(key, value) {
  this.key = key;
  this.value = value;
  this[NEWER] = undefined;
  this[OLDER] = undefined;
}
function EntryIterator(oldestEntry) {
  this.entry = oldestEntry;
}
EntryIterator.prototype[Symbol.iterator] = function () {
  return this;
};
EntryIterator.prototype.next = function () {
  var ent = this.entry;
  if (ent) {
    this.entry = ent[NEWER];
    return {
      done: false,
      value: [ent.key, ent.value]
    };
  }
  return {
    done: true,
    value: undefined
  };
};
function KeyIterator(oldestEntry) {
  this.entry = oldestEntry;
}
KeyIterator.prototype[Symbol.iterator] = function () {
  return this;
};
KeyIterator.prototype.next = function () {
  var ent = this.entry;
  if (ent) {
    this.entry = ent[NEWER];
    return {
      done: false,
      value: ent.key
    };
  }
  return {
    done: true,
    value: undefined
  };
};
function ValueIterator(oldestEntry) {
  this.entry = oldestEntry;
}
ValueIterator.prototype[Symbol.iterator] = function () {
  return this;
};
ValueIterator.prototype.next = function () {
  var ent = this.entry;
  if (ent) {
    this.entry = ent[NEWER];
    return {
      done: false,
      value: ent.value
    };
  }
  return {
    done: true,
    value: undefined
  };
};

// GoogleMutant by Iván Sánchez Ortega <ivan@sanchezortega.es>
function waitForAPI(callback, context) {
  var checkCounter = 0;
  var intervalId = null;
  intervalId = setInterval(function () {
    if (checkCounter >= 20) {
      clearInterval(intervalId);
      throw new Error('window.google not found after 10 seconds');
    }
    if (!!window.google && !!window.google.maps && !!window.google.maps.Map) {
      clearInterval(intervalId);
      callback.call(context);
    }
    ++checkCounter;
  }, 500);
}

// 🍂class GridLayer.GoogleMutant
// 🍂extends GridLayer
L.GridLayer.GoogleMutant = L.GridLayer.extend({
  options: {
    maxZoom: 21,
    // can be 23, but ugly if more than maxNativeZoom
    // 🍂option type: String = 'roadmap'
    // Google's map type. Valid values are 'roadmap', 'satellite' or 'terrain'. 'hybrid' is not really supported.
    type: 'roadmap',
    maxNativeZoom: 21
  },
  initialize: function initialize(options) {
    L.GridLayer.prototype.initialize.call(this, options);

    // Couple data structures indexed by tile key
    this._tileCallbacks = {}; // Callbacks for promises for tiles that are expected
    this._lru = new LRUMap(100); // Tile LRU cache

    this._imagesPerTile = this.options.type === 'hybrid' ? 2 : 1;
    this._boundOnMutatedImage = this._onMutatedImage.bind(this);
  },
  onAdd: function onAdd(map) {
    var _this = this;
    L.GridLayer.prototype.onAdd.call(this, map);
    this._initMutantContainer();

    // Attribution and logo nodes are not mutated a second time if the
    // mutant is removed and re-added to the map, hence they are
    // not cleaned up on layer removal, so they can be added here.
    if (this._logoContainer) {
      map._controlCorners.bottomleft.appendChild(this._logoContainer);
    }
    if (this._attributionContainer) {
      map._controlCorners.bottomright.appendChild(this._attributionContainer);
    }
    waitForAPI(function () {
      if (!_this._map) {
        return;
      }
      _this._initMutant();

      // handle layer being added to a map for which there are no Google tiles at the given zoom
      google.maps.event.addListenerOnce(_this._mutant, 'idle', function () {
        if (!_this._map) {
          return;
        }
        _this._checkZoomLevels();
        _this._mutantIsReady = true;
      });
    });
  },
  onRemove: function onRemove(map) {
    L.GridLayer.prototype.onRemove.call(this, map);
    this._observer.disconnect();
    map._container.removeChild(this._mutantContainer);
    if (this._logoContainer) {
      L.DomUtil.remove(this._logoContainer);
    }
    if (this._attributionContainer) {
      L.DomUtil.remove(this._attributionContainer);
    }
    if (this._mutant) {
      google.maps.event.clearListeners(this._mutant, 'idle');
    }
  },
  // 🍂method addGoogleLayer(name: String, options?: Object): this
  // Adds layer with the given name and options to the google Map instance.
  // `name`: one of the google maps API layers, with it's constructor available in `google.maps` object.
  // currently following values supported: 'TrafficLayer', 'TransitLayer', 'BicyclingLayer'.
  // `options`: see https://developers.google.com/maps/documentation/javascript/reference/map
  addGoogleLayer: function addGoogleLayer(googleLayerName, options) {
    var _this2 = this;
    if (!this._subLayers) {
      this._subLayers = {};
    }
    this.whenReady(function () {
      var Constructor = google.maps[googleLayerName];
      var googleLayer = new Constructor(options);
      googleLayer.setMap(_this2._mutant);
      _this2._subLayers[googleLayerName] = googleLayer;
    });
    return this;
  },
  // 🍂method removeGoogleLayer(name: String): this
  // Removes layer with the given name from the google Map instance.
  removeGoogleLayer: function removeGoogleLayer(googleLayerName) {
    var _this3 = this;
    this.whenReady(function () {
      var googleLayer = _this3._subLayers && _this3._subLayers[googleLayerName];
      if (googleLayer) {
        googleLayer.setMap(null);
        delete _this3._subLayers[googleLayerName];
      }
    });
    return this;
  },
  _initMutantContainer: function _initMutantContainer() {
    if (!this._mutantContainer) {
      this._mutantContainer = L.DomUtil.create('div', 'leaflet-google-mutant leaflet-top leaflet-left');
      this._mutantContainer.id = "_MutantContainer_".concat(L.Util.stamp(this._mutantContainer));
      this._mutantContainer.style.pointerEvents = 'none';
      this._mutantContainer.style.visibility = 'hidden';
      L.DomEvent.off(this._mutantContainer);
    }
    this._map.getContainer().appendChild(this._mutantContainer);
    this.setOpacity(this.options.opacity);
    var style = this._mutantContainer.style;
    if (this._map.options.zoomSnap < 1) {
      // Fractional zoom needs a bigger mutant container in order to load more (smaller) tiles
      style.width = '180%';
      style.height = '180%';
    } else {
      style.width = '100%';
      style.height = '100%';
    }
    style.zIndex = -1;
    this._attachObserver(this._mutantContainer);
  },
  _initMutant: function _initMutant() {
    if (this._mutant) {
      return;
    }
    var map = new google.maps.Map(this._mutantContainer, {
      center: {
        lat: 0,
        lng: 0
      },
      zoom: 0,
      tilt: 0,
      mapTypeId: this.options.type,
      disableDefaultUI: true,
      keyboardShortcuts: false,
      draggable: false,
      disableDoubleClickZoom: true,
      scrollwheel: false,
      styles: this.options.styles || {},
      backgroundColor: 'transparent'
    });
    this._mutant = map;
    this._update();

    // 🍂event spawned
    // Fired when the mutant has been created.
    this.fire('spawned', {
      mapObject: map
    });
    this._waitControls();
    this.once('controls_ready', this._setupAttribution);
  },
  _attachObserver: function _attachObserver(node) {
    if (!this._observer) {
      this._observer = new MutationObserver(this._onMutations.bind(this));
    }

    // pass in the target node, as well as the observer options
    this._observer.observe(node, {
      childList: true,
      subtree: true
    });

    // if we are reusing an old _mutantContainer, we must manually detect
    // all existing tiles in it
    Array.prototype.forEach.call(node.querySelectorAll('img'), this._boundOnMutatedImage);
  },
  _waitControls: function _waitControls() {
    var _this4 = this;
    var id = setInterval(function () {
      var layoutManager = _this4._mutant.__gm.layoutManager;
      if (!layoutManager) {
        return;
      }
      clearInterval(id);
      var positions;
      // iterate through obfuscated key names to find positions set (atm: layoutManager.o)
      Object.keys(layoutManager).forEach(function (key) {
        var el = layoutManager[key];
        if (el.get) {
          if (el.get(1) instanceof Node) {
            positions = el;
          }
        }
      });
      // 🍂event controls_ready
      // Fired when controls positions get available (passed in `positions` property).
      _this4.fire('controls_ready', {
        positions: positions
      });
    }, 50);
  },
  _setupAttribution: function _setupAttribution(ev) {
    // https://developers.google.com/maps/documentation/javascript/reference/control#ControlPosition
    var pos = google.maps.ControlPosition;
    var ctr = this._attributionContainer = ev.positions.get(pos.BOTTOM_RIGHT);
    L.DomUtil.addClass(ctr, 'leaflet-control leaflet-control-attribution');
    L.DomEvent.disableClickPropagation(ctr);
    ctr.style.height = '14px';
    this._map._controlCorners.bottomright.appendChild(ctr);
    this._logoContainer = ev.positions.get(pos.BOTTOM_LEFT);
    this._logoContainer.style.pointerEvents = 'auto';
    this._map._controlCorners.bottomleft.appendChild(this._logoContainer);
  },
  _onMutations: function _onMutations(mutations) {
    for (var i = 0; i < mutations.length; ++i) {
      var mutation = mutations[i];
      for (var j = 0; j < mutation.addedNodes.length; ++j) {
        var node = mutation.addedNodes[j];
        if (node instanceof HTMLImageElement) {
          this._onMutatedImage(node);
        } else if (node instanceof HTMLElement) {
          Array.prototype.forEach.call(node.querySelectorAll('img'), this._boundOnMutatedImage);
        }
      }
    }
  },
  // Only images which 'src' attrib match this will be considered for moving around.
  // Looks like some kind of string-based protobuf, maybe??
  // Only the roads (and terrain, and vector-based stuff) match this pattern
  _roadRegexp: /!1i(\d+)!2i(\d+)!3i(\d+)!/,
  // On the other hand, raster imagery matches this other pattern
  _satRegexp: /x=(\d+)&y=(\d+)&z=(\d+)/,
  // On small viewports, when zooming in/out, a static image is requested
  // This will not be moved around, just removed from the DOM.
  _staticRegExp: /StaticMapService\.GetMapImage/,
  _onMutatedImage: function _onMutatedImage(imgNode) {
    var coords;
    var match = imgNode.src.match(this._roadRegexp);
    var sublayer = 0;
    if (match) {
      coords = {
        z: match[1],
        x: match[2],
        y: match[3]
      };
      if (this._imagesPerTile > 1) {
        imgNode.style.zIndex = 1;
        sublayer = 1;
      }
    } else {
      match = imgNode.src.match(this._satRegexp);
      if (match) {
        coords = {
          x: match[1],
          y: match[2],
          z: match[3]
        };
      }
      // imgNode.style.zIndex = 0;
      sublayer = 0;
    }
    if (coords) {
      var tileKey = this._tileCoordsToKey(coords);
      imgNode.style.position = 'absolute';
      var key = "".concat(tileKey, "/").concat(sublayer);
      // Cache img so it can also be used in subsequent tile requests
      this._lru.set(key, imgNode);
      if (key in this._tileCallbacks && this._tileCallbacks[key]) {
        // Use the tile for *all* pending callbacks. They'll be cloned anyway.
        this._tileCallbacks[key].forEach(function (callback) {
          return callback(imgNode);
        });
        delete this._tileCallbacks[key];
      }
    }
  },
  createTile: function createTile(coords, done) {
    var key = this._tileCoordsToKey(coords);
    var tileContainer = L.DomUtil.create('div');
    tileContainer.style.textAlign = 'left';
    tileContainer.dataset.pending = this._imagesPerTile;
    done = done.bind(this, null, tileContainer);
    for (var i = 0; i < this._imagesPerTile; ++i) {
      var key2 = "".concat(key, "/").concat(i);
      var imgNode = this._lru.get(key2);
      if (imgNode) {
        tileContainer.appendChild(this._clone(imgNode));
        --tileContainer.dataset.pending;
      } else {
        this._tileCallbacks[key2] = this._tileCallbacks[key2] || [];
        this._tileCallbacks[key2].push(function (c /* , k2 */) {
          return function (imgNode) {
            c.appendChild(this._clone(imgNode));
            --c.dataset.pending;
            if (!parseInt(c.dataset.pending)) {
              done();
            }
          }.bind(this);
        }.bind(this)(tileContainer /* , key2 */));
      }
    }
    if (!parseInt(tileContainer.dataset.pending)) {
      L.Util.requestAnimFrame(done);
    }
    return tileContainer;
  },
  _clone: function _clone(imgNode) {
    var clonedImgNode = imgNode.cloneNode(true);
    clonedImgNode.style.visibility = 'visible';
    return clonedImgNode;
  },
  _checkZoomLevels: function _checkZoomLevels() {
    // setting the zoom level on the Google map may result in a different zoom level than the one requested
    // (it won't go beyond the level for which they have data).
    var zoomLevel = this._map.getZoom();
    var gMapZoomLevel = this._mutant.getZoom();
    if (!zoomLevel || !gMapZoomLevel) {
      return;
    }
    if (gMapZoomLevel !== zoomLevel // zoom levels are out of sync, Google doesn't have data
    || gMapZoomLevel > this.options.maxNativeZoom) {
      // at current location, Google does have data (contrary to maxNativeZoom)
      // Update maxNativeZoom
      this._setMaxNativeZoom(gMapZoomLevel);
    }
  },
  _setMaxNativeZoom: function _setMaxNativeZoom(zoomLevel) {
    if (zoomLevel !== this.options.maxNativeZoom) {
      this.options.maxNativeZoom = zoomLevel;
      this._resetView();
    }
  },
  _update: function _update(center) {
    // zoom level check needs to happen before super's implementation (tile addition/creation)
    // otherwise tiles may be missed if maxNativeZoom is not yet correctly determined
    if (this._mutant) {
      center = center || this._map.getCenter();
      var _center = new google.maps.LatLng(center.lat, center.lng);
      var zoom = Math.round(this._map.getZoom());
      var mutantZoom = this._mutant.getZoom();
      this._mutant.setCenter(_center);

      // ignore fractional zoom levels
      if (zoom !== mutantZoom) {
        this._mutant.setZoom(zoom);
        if (this._mutantIsReady) {
          this._checkZoomLevels();
        }
        // else zoom level check will be done later by 'idle' handler
      }
    }
    L.GridLayer.prototype._update.call(this, center);
  },
  // @method whenReady(fn: Function, context?: Object): this
  // Runs the given function `fn` when the mutant gets initialized, or immediately
  // if it's already initialized, optionally passing a function context.
  whenReady: function whenReady(callback, context) {
    if (this._mutant) {
      callback.call(context || this, {
        target: this
      });
    } else {
      this.on('spawned', callback, context);
    }
    return this;
  }
});

// 🍂factory gridLayer.googleMutant(options)
// Returns a new `GridLayer.GoogleMutant` given its options
L.gridLayer.googleMutant = function (options) {
  return new L.GridLayer.GoogleMutant(options);
};

var poiVisible = {
  type: 'roadmap'
};
var poiHided = {
  type: 'roadmap',
  styles: [{
    featureType: 'poi.business',
    stylers: [{
      visibility: 'off'
    }]
  }, {
    featureType: 'transit',
    elementType: 'labels.icon',
    stylers: [{
      visibility: 'off'
    }]
  }]
};

// required by a storybug (bad marker img url)
var myIcon = L$1.divIcon(_objectSpread2(_objectSpread2({}, L$1.Icon.Default.prototype.options), {}, {
  // default icon options
  // eslint-disable-next-line max-len
  html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 76.54 121.89"><path d="M38.27,121.82s0,.07,0,.07,38.23-60.24,38.23-80C76.54,12.72,57.16,0,38.27,0S0,12.72,0,41.85c0,19.8,38.25,80,38.25,80ZM25,40.2A13.27,13.27,0,1,1,38.27,53.81,13.44,13.44,0,0,1,25,40.2Z"/></svg>',
  iconSize: [27, 43],
  shadowUrl: false
}));
var portalDiv = document.createElement('div');
portalDiv.id = 'portal';
document.body.appendChild(portalDiv);
var popUpId = 'leaflet-popup';

// This component is required to handle the map center by marker clusters
function Layer(_ref) {
  var markers = _ref.markers,
    popupLoadingText = _ref.popupLoadingText,
    fromChina = _ref.fromChina,
    hidePoi = _ref.hidePoi,
    children = _ref.children,
    _ref$i18n = _ref.i18n,
    IntlProvider = _ref$i18n.IntlProvider,
    messages = _ref$i18n.messages,
    locale = _ref$i18n.locale;
  var parentMap = useMap();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    zoomHandled = _useState2[0],
    setzoomHandled = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    markersHandled = _useState4[0],
    setMarkersHandled = _useState4[1];
  var markersCluster = L$1.markerClusterGroup();
  var addCleanContainerForPopUp = function addCleanContainerForPopUp() {
    var createAndAddDivToCluster = function createAndAddDivToCluster() {
      var popUpDiv = document.createElement('div');
      popUpDiv.id = popUpId;
      document.body.appendChild(popUpDiv);
      markersCluster.bindPopup(popUpDiv);
    };
    if (!document.getElementById(popUpId)) {
      createAndAddDivToCluster();
    } else {
      var popUpDiv = document.getElementById(popUpId);
      popUpDiv.remove();
      createAndAddDivToCluster();
    }
  };
  useEffect(function () {
    if (fromChina) {
      L$1.tileLayer.chinaProvider('OSM.Normal.Map', {
        maxZoom: 18,
        minZoom: 0
      }).addTo(parentMap);
    } else {
      var mapOptions = !hidePoi ? poiHided : poiVisible;
      L$1.gridLayer.googleMutant(mapOptions).addTo(parentMap);
    }
  }, []); // eslint-disable-line

  useEffect(function () {
    if ((markers === null || markers === void 0 ? void 0 : markers.length) && !markersHandled) {
      markers.forEach(function (mark) {
        var marker = L$1.marker(new L$1.LatLng(mark.latitude, mark.longitude), {
          icon: myIcon
        }).on('click', function () {
          // Update popup
          if (children) {
            addCleanContainerForPopUp();
            // The children needs to access the i18n capabilities after a ReactDOM.render that looses the context
            ReactDOM.render(/*#__PURE__*/React__default.createElement(IntlProvider, {
              locale: locale,
              messages: messages
            }, cloneElement(children, _objectSpread2({
              key: mark.id,
              selectedProperty: {
                code: mark.id
              }
            }, mark))), document.getElementById('leaflet-popup'));
          }
        });
        markersCluster.addLayer(marker);
      });

      // TODO: check for the component loop bug: parentMap.addLayer(markersCluster);
      parentMap.addLayer(markersCluster);
      setMarkersHandled(true);
    }
  }, [markers, parentMap, popupLoadingText, markersHandled, children, locale, messages]); // eslint-disable-line

  useEffect(function () {
    // handle zoom & center
    if ((markers === null || markers === void 0 ? void 0 : markers.length) && parentMap && !zoomHandled) {
      var markerBounds = L$1.latLngBounds([]);
      markers.forEach(function (marker) {
        return markerBounds.extend([marker.latitude, marker.longitude]);
      });
      parentMap.fitBounds(markerBounds);
      setzoomHandled(true);
    }
  }, [parentMap, zoomHandled, markers]);
  return null;
}
Layer.defaultProps = {
  markers: null,
  popupLoadingText: 'Loading',
  hidePoi: false,
  i18n: {}
};
Layer.propTypes = {
  markers: markersShape,
  i18n: PropTypes.shape({}),
  popupLoadingText: PropTypes.string,
  hidePoi: PropTypes.bool
};

// https://github.com/htoooth/Leaflet.ChineseTmsProviders

L.TileLayer.ChinaProvider = L.TileLayer.extend({
  initialize: function initialize(type, options) {
    // (type, Object)
    var providers = L.TileLayer.ChinaProvider.providers;
    /* eslint-disable no-param-reassign */
    options = options || {};
    var parts = type.split('.');
    var providerName = parts[0];
    var mapName = parts[1];
    var mapType = parts[2];
    var url = providers[providerName][mapName][mapType];
    options.subdomains = providers[providerName].Subdomains;
    options.key = options.key || providers[providerName].key;
    if ('tms' in providers[providerName]) {
      options.tms = providers[providerName].tms;
    }
    L.TileLayer.prototype.initialize.call(this, url, options);
  }
});
var jawgAcessToken = 'L9HdbYz2ilGN617y7f6ErINBy78IOFUPKeRhJfBZNi4ax4Q7UPzLy4w6XsfrXk0l';
L.TileLayer.ChinaProvider.providers = {
  OSM: {
    Normal: {
      Map: '//{s}.tile.osm.org/{z}/{x}/{y}.png'
    },
    Subdomains: ['a', 'b', 'c']
  },
  // Jawg will be implemented in the future
  Jawg: {
    Terrain: {
      Map: "//{s}.tile.jawg.io/jawg-terrain/{z}/{x}/{y}{r}.png?access-token=".concat(jawgAcessToken)
    },
    Subdomains: ['a', 'b', 'c', 'd']
  }
};
L.tileLayer.chinaProvider = function (type, options) {
  return new L.TileLayer.ChinaProvider(type, options);
};

var DEFAULT_CENTER = [0, 0];
var DEFAULT_ZOOM = 0;
var OSMAttribution = "<a href=\"https://leafletjs.com\" title=\"A JS library for interactive maps\">Leaflet</a> \n    | &copy; <a href=\"http://osm.org/copyright\">OSM</a> contributors";
function Map$1(props) {
  var markers = props.markers,
    popupLoadingText = props.popupLoadingText,
    fromChina = props.fromChina,
    children = props.children,
    i18n = props.i18n,
    containerStyle = props.containerStyle;
  function renderMap() {
    return /*#__PURE__*/React__default.createElement(MapContainer, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      maxZoom: 18,
      style: containerStyle,
      attributionControl: !fromChina
    }, /*#__PURE__*/React__default.createElement(ZoomControl, {
      position: "bottomright"
    }), /*#__PURE__*/React__default.createElement(Layer, {
      markers: markers,
      popupLoadingText: popupLoadingText,
      fromChina: fromChina,
      i18n: i18n,
      hidePoi: true
    }, children), /*#__PURE__*/React__default.createElement(AttributionControl, {
      position: "bottomleft",
      prefix: fromChina ? OSMAttribution : ''
    }));
  }
  function render() {
    var _window, _window$google;
    if (!((_window = window) === null || _window === void 0 ? void 0 : (_window$google = _window.google) === null || _window$google === void 0 ? void 0 : _window$google.maps)) {
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, "Google Maps could not be found.");
    }
    return renderMap();
  }
  return render();
}
Map$1.defaultProps = {
  markers: null,
  popupLoadingText: 'Loading',
  fromChina: false,
  i18n: {},
  containerStyle: {
    width: '100%',
    height: '500px'
  }
};
Map$1.propTypes = {
  markers: markersShape,
  popupLoadingText: PropTypes.string,
  fromChina: PropTypes.bool,
  i18n: PropTypes.shape({}),
  children: PropTypes.node,
  containerStyle: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string
  })
};

export default Map$1;
//# sourceMappingURL=Leaflet.js.map
